// app 1.5.42 — the M® card: the words M's tab and the approver exchange, and the one sum both do.
//
// WYSIWYP — what you see is what you pay (Alexander, 30 Oct 2026; claude/multisig-card.md §9–9b). Above $X a payment
// needs a second signature from the M® card (a FIDO2 authenticator in card or key form) — and the figures the person
// reads while tapping it are drawn by the APPROVER, a page on its own origin whose code M's app does not control,
// which reads them from the transaction itself. The card's credential is bound to the approver's domain, so M's app
// cannot invoke the card at all.
//
// The round trip is one tab (never a third tab, never an iframe — §9b): M's tab → the approver → M's tab, with the
// payload in the URL fragment each way (a fragment never reaches a server). M's tab prepares and sponsors the real
// userOp first, so the hash the card signs is the hash Arc will execute.
//
//   M's tab → approver   #a=<base64url JSON Handoff>
//   approver → M's tab   #c=<base64url JSON Approval> | #x=<reason>   (a cancel, or a refusal)
//   M's tab → approver   #e=<base64url JSON EnrolRequest>              (enrolment: make the card's credential)
//   approver → M's tab   #k=<base64url JSON CardCredential>
//
// 1.5.63 (Alexander, 4 Nov: "Add M® card menu doesn't seem to time out … that's a security hole") — every trip is
// timed and the setup is bound to its tab. A request carries `at`, the moment M's tab left. The approver's screens
// time out CARD_SCREEN_MS after it (M's own idle logout) and go back to M by themselves. A setup request also
// carries `n`, a one-time code M keeps for the tab; the approver echoes it in the card, and M saves a card only for
// a live trip from this tab with the same code and wallet (card.ts acceptCardReturn). So a key tapped on an abandoned
// setup screen, or a crafted globalmoneyapp.com/#k=… link, sets nothing.
//
// The card signs H = sha256(fields ‖ userOpHash): the figures it displayed, bound to the op. The verifier — M's tab
// today (a stand-in, which a spiked app could skip); the account's module on Arc when it exists (§9, 2b) — recomputes
// the fields from the calldata and H from them, so the signed figures cannot diverge from what executes.

import { decodeFunctionData, erc20Abi, formatUnits, keccak256, sha256, concatHex, stringToHex, type Hex } from "viem";
import { getUserOperationHash } from "viem/account-abstraction";

export const CARD_PROTOCOL = 1;

/** 1.5.63 — the approver's screens time out this long after M's tab left: M's own idle logout (main.ts / idle.ts). */
export const CARD_SCREEN_MS = 3 * 60_000;
/** 1.5.63 — M accepts a card made on a setup started this recently (the screen's 3 minutes, then iOS's sheet). */
export const CARD_RETURN_MS = 5 * 60_000;
/** 1.5.63 — has a screen opened at `at` timed out? No `at` counts as timed out. */
export function screenTimedOut(at: number | undefined, now = Date.now()): boolean {
  return typeof at !== "number" || !Number.isFinite(at) || now - at > CARD_SCREEN_MS;
}

/** The userOp as M's tab hands it over: the RPC shape (hex strings), sponsored, unsigned. */
export interface HandoffOp {
  sender: Hex;
  nonce: Hex;
  callData: Hex;
  callGasLimit: Hex;
  verificationGasLimit: Hex;
  preVerificationGas: Hex;
  maxFeePerGas: Hex;
  maxPriorityFeePerGas: Hex;
  paymaster?: Hex;
  paymasterVerificationGasLimit?: Hex;
  paymasterPostOpGasLimit?: Hex;
  paymasterData?: Hex;
  factory?: Hex;
  factoryData?: Hex;
}

export interface Handoff {
  v: typeof CARD_PROTOCOL;
  /** where to return to (M's origin only — the approver refuses any other) */
  ret: string;
  chainId: number;
  entryPoint: Hex;
  op: HandoffOp;
  /** the userOp hash as M computed it; the approver recomputes and refuses a mismatch */
  hash: Hex;
  /** the card's credential id (base64url), bound to the approver's rpId */
  cred: string;
  /** 1.5.47 — the authenticator's transports from the enrolment (nfc, usb…) and its kind, so the approver's get() names
   *  them and iOS opens the card's own sheet, not the passkey chooser ("You don't have any passkeys…", 31 Oct) */
  transports?: string[];
  kind?: "card" | "key";
  /** what M claims about the payee — shown as M's claim, never as fact */
  /** 1.5.58 — `fromAddr`: the wallet M names in `from`; the approver shows that name only when it is the op's own sender */
  claim?: { name?: string; currency?: string; from?: string; fromAddr?: string };
  /** 1.5.63 — when M's tab left (ms); the approver's screen times out CARD_SCREEN_MS after it */
  at?: number;
}

/** The figures the approver showed and the card signed — derived from the calldata, never from M's claim. */
export interface ShownFields {
  /** the transfers in the op, in order: token, to, amount in units */
  transfers: { token: Hex; to: Hex; amount: string }[];
  sender: Hex;
  chainId: number;
}

export interface Approval {
  v: typeof CARD_PROTOCOL;
  hash: Hex;
  fields: ShownFields;
  cred: string;
  /** the WebAuthn assertion: webauthn-p256's shape */
  signature: Hex;
  webauthn: { authenticatorData: Hex; clientDataJSON: string; challengeIndex: number; typeIndex: number; userVerificationRequired: boolean };
}

export interface EnrolRequest {
  v: typeof CARD_PROTOCOL;
  ret: string;
  /** the wallet the card will be a second owner of */
  address: Hex;
  name: string;
  /** 1.5.63 — when M's tab left (ms): the setup screen times out CARD_SCREEN_MS after it */
  at: number;
  /** 1.5.63 — the one-time code M keeps for this tab's trip; echoed back in the card */
  n: string;
}

export interface CardCredential {
  v: typeof CARD_PROTOCOL;
  id: string;
  /** the P-256 public key, uncompressed, hex (0x04 ‖ x ‖ y) */
  publicKey: Hex;
  rpId: string;
  address: Hex;
  kind: "card" | "key";
  /** 1.5.47 — getTransports() at the enrolment, for the get() (optional: a card enrolled before 1.5.47 has none) */
  transports?: string[];
  /** 1.5.63 — on the way back from a setup only: the request's one-time code. Never saved. */
  n?: string;
}

// ---- the fragment ----------------------------------------------------------------------------------------------

export function encodeFragment(key: "a" | "c" | "e" | "k", value: unknown): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return `#${key}=${btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`;
}

export function decodeFragment<T>(hash: string, key: "a" | "c" | "e" | "k"): T | null {
  const m = new RegExp(`^#${key}=([A-Za-z0-9_-]+)$`).exec(hash);
  if (!m) return null;
  try {
    const b64 = m[1].replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (m[1].length % 4)) % 4);
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}

// ---- the sums --------------------------------------------------------------------------------------------------

const hexToBig = (h: Hex | undefined) => (h ? BigInt(h) : 0n);

/** The userOp hash from the RPC shape — the same sum viem does before signing (and sparekey/direct.ts). */
export function opHash(h: { chainId: number; entryPoint: Hex; op: HandoffOp }): Hex {
  const o = h.op;
  return getUserOperationHash({
    chainId: h.chainId,
    entryPointAddress: h.entryPoint,
    entryPointVersion: "0.7",
    userOperation: {
      sender: o.sender,
      nonce: hexToBig(o.nonce),
      callData: o.callData,
      callGasLimit: hexToBig(o.callGasLimit),
      verificationGasLimit: hexToBig(o.verificationGasLimit),
      preVerificationGas: hexToBig(o.preVerificationGas),
      maxFeePerGas: hexToBig(o.maxFeePerGas),
      maxPriorityFeePerGas: hexToBig(o.maxPriorityFeePerGas),
      paymaster: o.paymaster,
      paymasterVerificationGasLimit: o.paymasterVerificationGasLimit ? hexToBig(o.paymasterVerificationGasLimit) : undefined,
      paymasterPostOpGasLimit: o.paymasterPostOpGasLimit ? hexToBig(o.paymasterPostOpGasLimit) : undefined,
      paymasterData: o.paymasterData,
      factory: o.factory,
      factoryData: o.factoryData,
      signature: "0x",
    },
  });
}

// ERC-6900 v0.7 (Circle's modular account): execute(address,uint256,bytes) and executeBatch((address,uint256,bytes)[]).
const EXECUTE_ABI = [
  { type: "function", name: "execute", stateMutability: "payable", inputs: [{ name: "target", type: "address" }, { name: "value", type: "uint256" }, { name: "data", type: "bytes" }], outputs: [{ type: "bytes" }] },
  { type: "function", name: "executeBatch", stateMutability: "payable", inputs: [{ name: "calls", type: "tuple[]", components: [{ name: "target", type: "address" }, { name: "value", type: "uint256" }, { name: "data", type: "bytes" }] }], outputs: [{ type: "bytes[]" }] },
] as const;

/** What the op does — read from the calldata itself. Throws if a call is not an ERC-20 transfer (the approver refuses
 *  to show what it cannot read: an approval, a swap, a delegation — those need their own words, and until then no card). */
export function readTransfers(op: HandoffOp, chainId: number): ShownFields {
  const outer = decodeFunctionData({ abi: EXECUTE_ABI, data: op.callData });
  const calls: { target: Hex; value: bigint; data: Hex }[] =
    outer.functionName === "execute"
      ? [{ target: outer.args[0], value: outer.args[1], data: outer.args[2] }]
      : [...outer.args[0]];
  const transfers = calls.map((c) => {
    if (c.value !== 0n) throw new Error("card_unreadable: native value");
    const inner = decodeFunctionData({ abi: erc20Abi, data: c.data });
    if (inner.functionName !== "transfer") throw new Error(`card_unreadable: ${inner.functionName}`);
    return { token: c.target, to: inner.args[0], amount: inner.args[1].toString() };
  });
  if (!transfers.length) throw new Error("card_unreadable: no calls");
  return { transfers, sender: op.sender, chainId };
}

/** The bytes the card signs: sha256(canonical fields ‖ userOpHash). Canonical = the JSON of the fields with the keys in
 *  this order, lower-case addresses, decimal amounts — the same string on both sides. */
export function approvalChallenge(fields: ShownFields, hash: Hex): Hex {
  const canon = JSON.stringify({
    chainId: fields.chainId,
    sender: fields.sender.toLowerCase(),
    transfers: fields.transfers.map((t) => ({ token: t.token.toLowerCase(), to: t.to.toLowerCase(), amount: t.amount })),
  });
  return sha256(concatHex([stringToHex(canon), hash]));
}

/** For the record on Arc later: keccak of the same bytes (what a Solidity module would compute). */
export function approvalChallengeKeccak(fields: ShownFields, hash: Hex): Hex {
  const canon = JSON.stringify({
    chainId: fields.chainId,
    sender: fields.sender.toLowerCase(),
    transfers: fields.transfers.map((t) => ({ token: t.token.toLowerCase(), to: t.to.toLowerCase(), amount: t.amount })),
  });
  return keccak256(concatHex([stringToHex(canon), hash]));
}

/** The tokens the approver knows by address — Arc testnet. A transfer of any other token is shown by its address. */
export const KNOWN_TOKENS: Record<string, { code: string; symbol: string; decimals: number }> = {
  "0x3600000000000000000000000000000000000000": { code: "USD", symbol: "$", decimals: 6 },
  "0x89b50855aa3be2f677cd6303cec089b5f319d72a": { code: "EUR", symbol: "€", decimals: 6 },
  "0xa42e82b5d25e84d107cd8549ca432ef489cbad32": { code: "GBP", symbol: "£", decimals: 6 },
}; // the three M opens with (fx.ts CURRENCIES); the rest show by address until the approver carries the whole list

export function formatTransfer(t: { token: Hex; amount: string }): string {
  const k = KNOWN_TOKENS[t.token.toLowerCase()];
  const units = formatUnits(BigInt(t.amount), k?.decimals ?? 6);
  const n = Number(units);
  const num = n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return k ? `${k.symbol}${num}` : `${num} of ${t.token.slice(0, 6)}…${t.token.slice(-4)}`;
}

export const shortAddr = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
